import { useDeleteCanvas, useMoveCanvasInTree } from "@/api/canvas";
import type { FileRecord, MoveFileRecordPayload } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { cascadeDeleteFolder } from "../utils/folderDelete";
import { moveRecordCascading, type MoveOne } from "../utils/folderMove";

/**
 * Folder cascades with the canvas branch built in. A folder in the workspace
 * tree holds canvases as well as files, and those go through the canvas API —
 * every caller that moves or archives a folder uses this hook so none can
 * forget them.
 */
export function useFolderCascade() {
  const queryClient = useQueryClient();
  const moveCanvas = useMoveCanvasInTree();
  const deleteCanvas = useDeleteCanvas();

  const archiveCanvas = useCallback(
    (canvasId: string) => deleteCanvas.mutateAsync(canvasId),
    [deleteCanvas],
  );

  const moveRecord = useCallback(
    (params: {
      record: FileRecord;
      payload: MoveFileRecordPayload;
      allRecords: FileRecord[];
      moveOne: MoveOne;
    }) => moveRecordCascading({ ...params, moveCanvas }),
    [moveCanvas],
  );

  const deleteFolder = useCallback(
    (folder: FileRecord, allRecords: FileRecord[], workspaceId?: string) =>
      cascadeDeleteFolder(
        folder,
        allRecords,
        queryClient,
        workspaceId,
        archiveCanvas,
      ),
    [queryClient, archiveCanvas],
  );

  return useMemo(
    () => ({
      moveRecordCascading: moveRecord,
      cascadeDeleteFolder: deleteFolder,
      moveCanvas,
      archiveCanvas,
    }),
    [moveRecord, deleteFolder, moveCanvas, archiveCanvas],
  );
}
